// src/requestFrame.ts
function requestFrame(callback, timeout = 0) {
  window.requestAnimationFrame((t) => {
    setTimeout(() => {
      callback(t);
    }, timeout);
  });
}
function requestFramePromise(timeout = 0) {
  return new Promise((resolve) => {
    requestFrame(() => {
      resolve(true);
    }, timeout);
  });
}

// src/asyncRequestFrame.ts
async function asyncRequestFrame() {
  return new Promise((resolve) => requestFrame(resolve));
}

// src/asyncTimeout.ts
async function asyncTimeout(timeout = 0) {
  return new Promise((resolve) => setTimeout(resolve, timeout));
}

// src/asyncTransitionTimeout.ts
async function asyncTransitionTimeout(timeout) {
  return new Promise((resolve) => setTimeout(resolve, timeout));
}

// src/createElementFromHtmlString.ts
function createElementFromHTML(htmlString) {
  const div = document.createElement("div");
  div.innerHTML = htmlString.trim();
  return div.firstChild;
}
function createElementsFromHTML(htmlString) {
  const div = document.createElement("div");
  div.innerHTML = htmlString.trim();
  return Array.from(div.childNodes).map((x) => x);
}

// src/css3VariableHelper.ts
function getVariableContent(varName, baseEl = document.documentElement) {
  return window.getComputedStyle(baseEl).getPropertyValue(varName).trim();
}

// src/detectBrowser.ts
var isIE11 = () => !!window.msCrypto;
var isSafari = () => typeof navigator.vendor !== "undefined" && navigator.vendor.indexOf("Apple") > -1 && typeof navigator.userAgent !== "undefined" && navigator.userAgent.indexOf("CriOS") === -1 && navigator.userAgent.indexOf("FxiOS") === -1;

// src/extractBgImageUrl.ts
function extractBgImageUrl(el, rendered = true) {
  const backgroundImage = rendered ? window.getComputedStyle(el, null).backgroundImage : el.style.backgroundImage;
  if (!backgroundImage) {
    return false;
  }
  const re = /url\((['"])?(.*?)\1\)/gi;
  const matches = re.exec(backgroundImage);
  if (!matches || !matches[2]) {
    return false;
  }
  return matches[2];
}

// src/focusFirstElement.ts
import { toValue } from "vue";

// src/getFocusableElements.ts
var getFocusableElements = (element = document) => [
  ...element.querySelectorAll('a, button, input:not([type="hidden"]), textarea, select, details,[tabindex]:not([tabindex="-1"])')
].filter((el) => !el.hasAttribute("disabled")).filter((el) => el.getAttribute("tabindex") !== "-1");

// src/focusFirstElement.ts
var focusFirstElement = (ofTarget) => {
  const target = toValue(ofTarget);
  if (!target) {
    return;
  }
  const focusableElements = getFocusableElements(target);
  if (focusableElements.length === 0) {
    return;
  }
  focusableElements[0]?.focus();
};

// src/focusLastElement.ts
import { toValue as toValue2 } from "vue";
var focusLastElement = (ofTarget) => {
  const target = toValue2(ofTarget);
  if (!target) {
    return;
  }
  const focusableElements = getFocusableElements(target);
  if (focusableElements.length === 0) {
    return;
  }
  focusableElements[focusableElements.length - 1]?.focus();
};

// src/focusNextElement.ts
import { toValue as toValue3 } from "vue";
var focusNextElement = (ofTarget, excludeEl = null) => {
  const target = toValue3(ofTarget);
  const excludeElement = toValue3(excludeEl);
  if (!target) {
    return;
  }
  const focusableElements = getFocusableElements();
  if (focusableElements.length === 0) {
    return;
  }
  let targetIndex = focusableElements.indexOf(target) + 1;
  let targetElement = focusableElements[targetIndex];
  let isVisible = true;
  if (typeof targetElement["checkVisibility"] === "function") {
    isVisible = targetElement["checkVisibility"]({
      checkOpacity: true,
      // Check CSS opacity property too
      checkVisibilityCSS: true
      // Check CSS visibility property too
    });
    if (isVisible) {
      isVisible = typeof targetElement?.parentElement !== void 0 && (targetElement?.parentElement)["checkVisibility"]({
        checkOpacity: true,
        // Check CSS opacity property too
        checkVisibilityCSS: true
        // Check CSS visibility property too
      });
    }
    while (typeof targetElement === "undefined" || (excludeElement !== null && excludeElement.contains(targetElement) || !isVisible) && targetIndex + 1 < focusableElements.length - 1) {
      targetIndex++;
      targetElement = focusableElements[targetIndex];
      isVisible = targetElement["checkVisibility"]({
        checkOpacity: true,
        // Check CSS opacity property too
        checkVisibilityCSS: true
        // Check CSS visibility property too
      });
    }
  }
  if (isVisible) {
    focusableElements[targetIndex]?.focus();
  } else {
    target.focus();
  }
};

// src/FocusTrap.ts
var FocusTrap = class {
  constructor(beforeEl, afterEl, beforeCallback = () => {
  }, afterCallback = () => {
  }, within = false) {
    this.beforeCallback = beforeCallback;
    this.afterCallback = afterCallback;
    this.trapBefore = document.createElement("div");
    this.trapBefore.setAttribute("tabindex", "0");
    this.trapBefore.addEventListener("focus", this.beforeCallback);
    if (within) {
      beforeEl.prepend(this.trapBefore);
    } else {
      beforeEl.before(this.trapBefore);
    }
    this.trapAfter = document.createElement("div");
    this.trapAfter.setAttribute("tabindex", "0");
    this.trapAfter.addEventListener("focus", this.afterCallback);
    if (within) {
      afterEl.append(this.trapAfter);
    } else {
      afterEl.after(this.trapAfter);
    }
  }
  destroy() {
    this.trapBefore.removeEventListener("focus", this.beforeCallback);
    this.trapBefore.remove();
    this.trapAfter.removeEventListener("focus", this.afterCallback);
    this.trapAfter.remove();
  }
};

// src/getParents.ts
var getParents = (el) => {
  const parents = [];
  for (; el && el !== document; el = el.parentNode) {
    parents.push(el);
  }
  return parents;
};

// src/getResizeObserverEntryDimensions.ts
function getResizeObserverEntryDimensions(entry) {
  let width;
  let height;
  if (entry.contentBoxSize) {
    const contentBoxSize = Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0] : entry.contentBoxSize;
    width = contentBoxSize.inlineSize;
    height = contentBoxSize.blockSize;
  } else {
    width = entry.contentRect.width;
    height = entry.contentRect.height;
  }
  return {
    width,
    height
  };
}

// src/getScrollbarWidth.ts
var getScrollbarWidth = () => {
  const scrollDiv = document.createElement("div");
  scrollDiv.style.position = "absolute";
  scrollDiv.style.top = "-9999px";
  scrollDiv.style.opacity = "0";
  scrollDiv.style.width = "100px";
  scrollDiv.style.height = "100px";
  scrollDiv.style.overflow = "scroll";
  document.body.appendChild(scrollDiv);
  const width = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  document.body.removeChild(scrollDiv);
  return width;
};

// src/getScrollOffset.ts
var getScrollOffset = () => {
  const offset = getVariableContent("--scroll-offset-top").split("px").join("") ?? "0";
  return parseInt(offset, 10);
};

// src/groupParamsByKey.ts
var groupParamsByKey = (params, removeArrayFromKey = false) => [...params.entries()].reduce((acc, tuple) => {
  let [key, val] = tuple;
  if (removeArrayFromKey) {
    const arrayStartPos = key.indexOf("[");
    if (arrayStartPos !== -1) {
      key = key.substring(0, arrayStartPos);
    }
  }
  if (acc.hasOwnProperty(key)) {
    if (Array.isArray(acc[key])) {
      acc[key] = [...acc[key], val];
    } else {
      if (acc[key] !== "") {
        acc[key] = [acc[key], val];
      } else {
        acc[key] = [val];
      }
    }
  } else {
    acc[key] = val;
  }
  return acc;
}, {});

// src/initializeComponent.ts
function initializeComponent(cmp, baseElement = document, eventBus) {
  if (!cmp.selector) {
    console.warn('Component definition has no "selector".', cmp);
    return null;
  }
  if (typeof cmp.onlyIf === "function" && !cmp.onlyIf()) {
    return null;
  }
  const component = {
    ...cmp,
    instances: []
  };
  const els = baseElement.querySelectorAll(cmp.selector);
  Array.from(els).forEach((el) => {
    const instance = new cmp.cls(el, {
      ...cmp.options || {}
    }, eventBus);
    component.instances.push(instance);
  });
  return component;
}

// src/keycodes.ts
var keycodes = {
  LEFT_ARROW: 37,
  UP_ARROW: 38,
  RIGHT_ARROW: 39,
  DOWN_ARROW: 40,
  BACKSPACE: 8,
  ESCAPE: 27,
  SPACE: 32,
  ENTER: 13,
  TAB: 9,
  HOME: 36,
  END: 35,
  PAGE_UP: 33,
  PAGE_DOWN: 34,
  Z: 90
};
var keyMap = {
  ArrowLeft: keycodes.LEFT_ARROW,
  Left: keycodes.LEFT_ARROW,
  ArrowRight: keycodes.RIGHT_ARROW,
  Right: keycodes.RIGHT_ARROW,
  ArrowUp: keycodes.UP_ARROW,
  Up: keycodes.UP_ARROW,
  ArrowDown: keycodes.DOWN_ARROW,
  Down: keycodes.DOWN_ARROW,
  Escape: keycodes.ESCAPE,
  " ": keycodes.SPACE,
  Spacebar: keycodes.SPACE,
  Enter: keycodes.ENTER,
  Tab: keycodes.TAB,
  Home: keycodes.HOME,
  End: keycodes.END,
  PageUp: keycodes.PAGE_UP,
  PageDown: keycodes.PAGE_DOWN,
  z: keycodes.Z,
  Z: keycodes.Z
};
function getKeycode(event) {
  const key = event.key || event.keyCode || event.which;
  if (typeof key === "number" && isFinite(key) && Math.floor(key) === key) {
    return key;
  }
  return keyMap[key];
}

// src/stopEvent.ts
var stopEvent = function(event) {
  if (typeof event.stopPropagation !== "undefined") {
    event.stopPropagation();
  } else {
    if (typeof event.cancelBubble !== "undefined") {
      event.cancelBubble = true;
    }
  }
  if (typeof event.preventDefault !== "undefined") {
    event.preventDefault();
  }
};

// src/throttle.ts
function throttle(callback, wait, immediate = false) {
  let timeout = null;
  let initialCall = true;
  return function() {
    const callNow = immediate && initialCall;
    const next = () => {
      callback.apply(null, arguments);
      timeout = null;
    };
    if (callNow) {
      initialCall = false;
      next();
    }
    if (!timeout) {
      timeout = window.setTimeout(next, wait);
    }
  };
}

// src/whatInput.ts
var whatInput = () => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return {
      // always return "initial" because no interaction will ever be detected
      ask: () => "initial",
      // always return null
      element: () => null,
      // no-op
      ignoreKeys: () => {
      },
      // no-op
      specificKeys: () => {
      },
      // no-op
      registerOnChange: () => {
      },
      // no-op
      unRegisterOnChange: () => {
      }
    };
  }
  const docElem = document.documentElement;
  let currentElement = null;
  let currentInput = "initial";
  let currentIntent = currentInput;
  let currentTimestamp = Date.now();
  const formInputs = ["button", "input", "select", "textarea"];
  const functionList = [];
  let ignoreMap = [
    16,
    // shift
    17,
    // control
    18,
    // alt
    91,
    // Windows key / left Apple cmd
    93
    // Windows menu / right Apple cmd
  ];
  let specificMap = [];
  const inputMap = {
    keydown: "keyboard",
    keyup: "keyboard",
    mousedown: "mouse",
    mousemove: "mouse",
    MSPointerDown: "pointer",
    MSPointerMove: "pointer",
    pointerdown: "pointer",
    pointermove: "pointer",
    touchstart: "touch",
    touchend: "touch"
  };
  let isScrolling = false;
  const mousePos = {
    x: null,
    y: null
  };
  const pointerMap = {
    2: "touch",
    3: "touch",
    // treat pen like touch
    4: "mouse"
  };
  let supportsPassive = false;
  try {
    const opts = Object.defineProperty({}, "passive", {
      get: () => {
        supportsPassive = true;
      }
    });
    window.addEventListener("test", null, opts);
  } catch (e) {
  }
  const setUp = () => {
    inputMap[detectWheel()] = "mouse";
    addListeners();
  };
  const addListeners = () => {
    const options = supportsPassive ? { passive: true } : false;
    document.addEventListener("DOMContentLoaded", setPersist);
    if (window.PointerEvent) {
      window.addEventListener("pointerdown", setInput);
    } else {
      if (window.MSPointerEvent) {
        window.addEventListener("MSPointerDown", setInput);
      } else {
        window.addEventListener("mousedown", setInput);
        if ("ontouchstart" in window) {
          window.addEventListener("touchstart", setInput, options);
          window.addEventListener("touchend", setInput);
        }
      }
    }
    window.addEventListener("keydown", setInput);
    window.addEventListener("keyup", setInput);
    window.addEventListener("focusin", setElement);
    window.addEventListener("focusout", clearElement);
  };
  const setPersist = () => {
    doUpdate("input");
  };
  const setInput = (event) => {
    const eventKey = event.which;
    let value = inputMap[event.type];
    if (value === "pointer") {
      value = pointerType(event);
    }
    const ignoreMatch = !specificMap.length && ignoreMap.indexOf(eventKey) === -1;
    const specificMatch = specificMap.length && specificMap.indexOf(eventKey) !== -1;
    let shouldUpdate = value === "keyboard" && eventKey && (ignoreMatch || specificMatch) || value === "mouse" || value === "touch";
    if (validateTouch(value)) {
      shouldUpdate = false;
    }
    if (shouldUpdate && currentInput !== value) {
      currentInput = value;
      doUpdate("input");
    }
  };
  const doUpdate = (which) => {
    docElem.setAttribute(
      `data-w${which === "input" ? "i" : which === "intent" ? "in" : which}`,
      which === "input" ? currentInput : currentIntent
    );
    fireFunctions(which);
  };
  const setIntent = (event) => {
    let value = inputMap[event.type];
    if (value === "pointer") {
      value = pointerType(event);
    }
    detectScrolling(event);
  };
  const setElement = (event) => {
    if (!event.target.nodeName) {
      clearElement();
      return;
    }
    currentElement = event.target.nodeName.toLowerCase();
    if (event.target.classList && event.target.classList.length) {
    }
  };
  const clearElement = () => {
    currentElement = null;
  };
  const pointerType = (event) => {
    if (typeof event.pointerType === "number") {
      return pointerMap[event.pointerType];
    }
    return event.pointerType === "pen" ? "touch" : event.pointerType;
  };
  const validateTouch = (value) => {
    const timestamp = Date.now();
    const touchIsValid = value === "mouse" && currentInput === "touch" && timestamp - currentTimestamp < 200;
    currentTimestamp = timestamp;
    return touchIsValid;
  };
  const detectWheel = () => {
    let wheelType = null;
    if ("onwheel" in document.createElement("div")) {
      wheelType = "wheel";
    } else {
      wheelType = document.onmousewheel !== void 0 ? "mousewheel" : "DOMMouseScroll";
    }
    return wheelType;
  };
  const fireFunctions = (type) => {
    for (let i = 0, len = functionList.length; i < len; i++) {
      if (functionList[i].type === type) {
        functionList[i].fn.call(
          void 0,
          type === "input" ? currentInput : currentIntent
        );
      }
    }
  };
  const objPos = (match) => {
    for (let i = 0, len = functionList.length; i < len; i++) {
      if (functionList[i].fn === match) {
        return i;
      }
    }
  };
  const detectScrolling = (event) => {
    if (mousePos.x !== event.screenX || mousePos.y !== event.screenY) {
      isScrolling = false;
      mousePos.x = event.screenX;
      mousePos.y = event.screenY;
    } else {
      isScrolling = true;
    }
  };
  const checkClosest = (elem, tag) => {
    const ElementPrototype = window.Element.prototype;
    if (!ElementPrototype.matches) {
      ElementPrototype.matches = ElementPrototype.msMatchesSelector || ElementPrototype.webkitMatchesSelector;
    }
    if (!ElementPrototype.closest) {
      do {
        if (elem.matches(tag)) {
          return elem;
        }
        elem = elem.parentElement || elem.parentNode;
      } while (elem !== null && elem.nodeType === 1);
      return null;
    }
    return elem.closest(tag);
  };
  if ("addEventListener" in window) {
    setUp();
  }
  return {
    // returns string: the current input type
    // opt: 'intent'|'input'
    // 'input' (default): returns the same value as the `data-whatinput` attribute
    // 'intent': includes `data-whatintent` value if it's different than `data-whatinput`
    ask: (opt) => opt === "intent" ? currentIntent : currentInput,
    // returns string: the currently focused element or null
    element: () => currentElement,
    // overwrites ignored keys with provided array
    ignoreKeys: (arr) => {
      ignoreMap = arr;
    },
    // overwrites specific char keys to update on
    specificKeys: (arr) => {
      specificMap = arr;
    },
    // attach functions to input and intent "events"
    // funct: function to fire on change
    // eventType: 'input'|'intent'
    registerOnChange: (fn, eventType) => {
      functionList.push({ fn, type: eventType || "input" });
    },
    unRegisterOnChange: (fn) => {
      const position = objPos(fn);
      if (position || position === 0) {
        functionList.splice(position, 1);
      }
    },
    clearStorage: () => {
      window.sessionStorage.clear();
    }
  };
};

// src/windowHasScrollbar.ts
var windowHasScrollbar = (vertical = true) => {
  if (vertical) {
    return document.body.clientHeight > document.documentElement.clientHeight;
  }
  return document.body.clientWidth > document.documentElement.clientWidth;
};
export {
  FocusTrap,
  asyncRequestFrame,
  asyncTimeout,
  asyncTransitionTimeout,
  createElementFromHTML,
  createElementsFromHTML,
  extractBgImageUrl,
  focusFirstElement,
  focusLastElement,
  focusNextElement,
  getFocusableElements,
  getKeycode,
  getParents,
  getResizeObserverEntryDimensions,
  getScrollOffset,
  getScrollbarWidth,
  getVariableContent,
  groupParamsByKey,
  initializeComponent,
  isIE11,
  isSafari,
  keyMap,
  keycodes,
  requestFrame,
  requestFramePromise,
  stopEvent,
  throttle,
  whatInput,
  windowHasScrollbar
};
